import React, { ReactNode } from 'react';

import { S3ImageWrapper } from '@/components/shared/S3ImageWrapper/S3ImageWrapper';
import { classNames } from '@/helpers/classNames';
import { useAuth } from '@/providers/AuthProvider';
import { useTeam } from '@/providers/TeamProvider/TeamProvider';
import {
	CircularProgress,
	Avatar as MuiAvatar,
	SvgIcon,
} from '@material-ui/core';
import { isNil } from 'ramda';

import { AsaphAvatarBackgroundTypes } from '../shared/Avatar';
import styles from './UserAvatar.module.scss';

type S3Object = any;

export type AvatarSize =
	| 'xxxx-small'
	| 'xxx-small'
	| 'xx-small'
	| 'x-small'
	| 'small'
	| 'medium'
	| 'large'
	| 'x-large'
	| 'xx-large'
	| 'xxx-large';

export interface AvatarProps {
	userId: string;
	size: AvatarSize;
	background?: AsaphAvatarBackgroundTypes;
	progress?: number;
	additionalProgress?: number;
	className?: string;
	hasNotification?: boolean;
	notificationSize?: 'small' | 'medium';
	activeSize?: 'x-small' | 'small' | 'medium';
	borderColor?: 'body-background' | 'sheet';
	showActiveStatus?: boolean;
}

const getProgressSizes = (size: AvatarSize) => {
	switch (size) {
		case 'xxxx-small':
			return {
				size: 15,
				thickness: 1,
			};
		case 'xxx-small':
			return {
				size: 22,
				thickness: 1,
			};
		case 'xx-small':
			return {
				size: 33,
				thickness: 1,
			};
		case 'x-small':
			return {
				size: 46,
				thickness: 1.5,
			};
		case 'small':
			return {
				size: 58,
				thickness: 1.5,
			};
		case 'medium':
			return {
				size: 74,
				thickness: 2,
			};
		case 'large':
			return {
				size: 91,
				thickness: 2,
			};
		case 'x-large':
			return {
				size: 122,
				thickness: 2,
			};
		case 'xx-large':
			return {
				size: 158,
				thickness: 2,
			};
		case 'xxx-large':
			return {
				size: 182,
				thickness: 2,
			};
		default:
			throw new Error('Unknown size provided');
	}
};

export const UserAvatar = ({
	userId,
	size,
	background = 'default',
	progress,
	additionalProgress,
	className,
	hasNotification,
	notificationSize = 'small',
	activeSize = 'small',
	borderColor,
	showActiveStatus = true,
}: AvatarProps) => {
	const { user: authUser } = useAuth();
	const {
		team: { members },
	} = useTeam();
	// const classes = getStyles(size);
	const progressSizes = getProgressSizes(size);
	const user = members.find((m) => m.user.id === userId)?.user;

	const nameInitials = user?.displayName || '?';
	const finalBackground = user?.color || background;
	const isActive = authUser.id === userId || user?.isActive;

	return (
		<div
			className={classNames([
				styles['root'],
				styles[`size-${size}`],
				className,
			])}
		>
			<div
				className={classNames([
					styles['avatar'],
					styles[`background-${finalBackground}`],
					borderColor && styles[`border-${borderColor}`],
				])}
			>
				<div
					className={classNames([
						styles['text'],
						styles[`size-${size}`],
					])}
				>
					{nameInitials[0]}
				</div>
			</div>
			{hasNotification && (
				<div
					className={classNames([
						styles['notification'],
						styles[`size-${notificationSize}`],
					])}
				/>
			)}
			{showActiveStatus && isActive && (
				<div
					className={classNames([
						styles['active'],
						styles[`size-${activeSize}`],
					])}
				/>
			)}
			{!isNil(progress) && (
				<>
					{!isNil(additionalProgress) && (
						<div
							className={`${styles['progress']} ${styles['progress--additional']}`}
						>
							<CircularProgress
								variant="determinate"
								value={additionalProgress}
								color="secondary"
								size={progressSizes.size}
								thickness={progressSizes.thickness}
							/>
						</div>
					)}
					<div className={styles['progress']}>
						<CircularProgress
							variant="determinate"
							value={progress}
							size={progressSizes.size}
							thickness={progressSizes.thickness}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default UserAvatar;
